import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Fade,
  Skeleton,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

export interface Column {
  id: string;
  label: string;
  format?: (value: any) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
}

export interface Action {
  icon: React.ReactNode;
  tooltip: string;
  onClick: (id: string) => void;
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  disabled?: (row: any) => boolean;
  hide?: (row: any) => boolean;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  idField?: string;
  actions?: Action[];
  pagination?: {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
  };
  sorting?: {
    orderBy: string;
    order: 'asc' | 'desc';
    onSortChange: (orderBy: string, order: 'asc' | 'desc') => void;
  };
  loading?: boolean;
  emptyText?: string;
  dense?: boolean;
  selectable?: boolean;
  selected?: string[];
  onSelectionChange?: (selected: string[]) => void;
}

export default function DataTable({
  columns,
  data,
  idField = 'id',
  actions,
  pagination,
  sorting,
  loading = false,
  emptyText = 'Нет данных для отображения',
  dense = false,
  selectable = false,
  selected = [],
  onSelectionChange
}: DataTableProps) {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const allSelected = data.length > 0 && data.every(row => selected.includes(row[idField]));
  const someSelected = selected.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : data.map(row => row[idField]));
  };

  const handleRowSelect = (rowId: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selected.includes(rowId)
        ? selected.filter(x => x !== rowId)
        : [...selected, rowId]
    );
  };

  const handleSortRequest = (columnId: string) => {
    if (!sorting) return;

    const isAsc = sorting.orderBy === columnId && sorting.order === 'asc';
    sorting.onSortChange(columnId, isAsc ? 'desc' : 'asc');
  };

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }
      }}
    >
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow
              sx={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              }}
            >
              {selectable && (
                <TableCell padding="checkbox" sx={{ borderBottom: '2px solid #667eea' }}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#667eea',
                      '&.Mui-checked': { color: '#667eea' },
                      '&.MuiCheckbox-indeterminate': { color: '#667eea' },
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sortDirection={sorting && sorting.orderBy === column.id ? sorting.order : false}
                  sx={{
                    fontWeight: 700,
                    color: '#667eea',
                    borderBottom: '2px solid #667eea',
                    py: 2,
                  }}
                >
                  {column.sortable && sorting ? (
                    <TableSortLabel
                      active={sorting.orderBy === column.id}
                      direction={sorting.orderBy === column.id ? sorting.order : 'asc'}
                      onClick={() => handleSortRequest(column.id)}
                      sx={{
                        '&.Mui-active': {
                          color: '#667eea',
                          '& .MuiTableSortLabel-icon': {
                            color: '#667eea',
                          }
                        }
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions && actions.length > 0 && (
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 700,
                    color: '#667eea',
                    borderBottom: '2px solid #667eea',
                    py: 2,
                  }}
                >
                  Действия
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)} align="center">
                  <Box sx={{ py: 4 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton
                        key={i}
                        height={dense ? 40 : 60}
                        sx={{ mb: 1 }}
                        animation="wave"
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)} align="center">
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <StorageIcon
                      sx={{
                        fontSize: 64,
                        color: 'text.secondary',
                        opacity: 0.3,
                        mb: 2
                      }}
                    />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {emptyText}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <Fade in key={row[idField]} timeout={300}>
                  <TableRow
                    hover
                    onMouseEnter={() => setHoveredRow(row[idField])}
                    onMouseLeave={() => setHoveredRow(null)}
                    sx={{
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(102, 126, 234, 0.04)',
                        transform: 'scale(1.001)',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(row[idField])}
                          onChange={() => handleRowSelect(row[idField])}
                          sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || 'left'}
                        sx={{
                          py: dense ? 1.5 : 2,
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        }}
                      >
                        {column.format ? column.format(row[column.id]) : row[column.id]}
                      </TableCell>
                    ))}
                    {actions && actions.length > 0 && (
                      <TableCell
                        align="right"
                        sx={{
                          py: dense ? 1.5 : 2,
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                            opacity: hoveredRow === row[idField] ? 1 : 0.7,
                            transition: 'opacity 0.2s',
                          }}
                        >
                          {actions.map((action, index) => {
                            if (action.hide && action.hide(row)) {
                              return null;
                            }

                            const disabled = action.disabled ? action.disabled(row) : false;

                            return (
                              <Tooltip key={index} title={action.tooltip}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={action.color || 'primary'}
                                    onClick={() => action.onClick(row[idField])}
                                    disabled={disabled}
                                    sx={{
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                      }
                                    }}
                                  >
                                    {action.icon}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </Fade>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <Box
          sx={{
            borderTop: '1px solid rgba(224, 224, 224, 0.5)',
            background: 'rgba(102, 126, 234, 0.02)',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.count}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
            onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
            labelRowsPerPage="Строк на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
}